import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { solicitudesAPI, exportAPI } from '../utils/api';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Plus, Download, FileText, ClipboardList, ChevronRight, X, Filter, RefreshCw, Loader2, Zap, CalendarClock } from 'lucide-react';
import { toast } from 'sonner';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../components/ui/tooltip';
import SolicitudDetailDialog from '../components/SolicitudDetailDialog';
import CreateSolicitudDialog from '../components/CreateSolicitudDialog';
import InspeccionDialog from '../components/InspeccionDialog';
import AtencionDialog from '../components/AtencionDialog';
import Pagination from '../components/Pagination';
import { usePermissions } from '../hooks/usePermissions';

// Intervalo de polling en milisegundos (30 segundos)
const POLLING_INTERVAL = 30000;
// Registros por página
const PAGE_SIZE = 20;

const Solicitudes = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [solicitudes, setSolicitudes] = useState([]);
  const [loading, setLoading] = useState(true);  // Solo para carga inicial
  const [tableLoading, setTableLoading] = useState(false);  // Para filtros/búsqueda (sutil)
  const [loadingPasoSiguiente, setLoadingPasoSiguiente] = useState(null);
  const [selectedSolicitud, setSelectedSolicitud] = useState(null);
  const [showDetail, setShowDetail] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [showInspeccion, setShowInspeccion] = useState(false);
  const [showAtencion, setShowAtencion] = useState(false);
  const { isAdmin, canCreate, canView } = usePermissions();
  
  // Estado de paginación
  const [pagination, setPagination] = useState({
    page: 1,
    limit: PAGE_SIZE,
    total: 0,
    total_pages: 1,
    has_next: false,
    has_prev: false
  });
  
  // Estado de búsqueda local — inicializar desde URL si viene ?search=
  const [searchTerm, setSearchTerm] = useState(searchParams.get('search') || '');
  const searchTermRef = useRef(searchParams.get('search') || ''); // Ref sincronizado para uso en callbacks
  const searchTimeoutRef = useRef(null);
  const abortControllerRef = useRef(null); // Para cancelar peticiones pendientes
  const requestIdRef = useRef(0); // ID único para cada petición
  const isInitialLoadRef = useRef(true); // Para distinguir carga inicial vs filtros
  
  // Refs para polling inteligente
  const pollingIntervalRef = useRef(null);
  const isPollingActiveRef = useRef(true);

  // URL filter params
  const urlEstado = searchParams.get('estado');
  const urlDias = searchParams.get('dias');
  const urlSupervisor = searchParams.get('supervisor');
  const urlProyecto = searchParams.get('proyecto');
  const urlEtapa = searchParams.get('etapa');
  const urlOrigenes = searchParams.get('origenes');
  const urlId = searchParams.get('id');
  const urlSearch = searchParams.get('search');

  // Check if there are active URL filters
  const hasUrlFilters = urlEstado || urlDias || urlSupervisor || urlProyecto || urlEtapa || urlOrigenes;

  // Función de carga paginada con filtros del servidor
  // Usa requestId para ignorar respuestas obsoletas (race condition fix)
  const loadSolicitudes = useCallback(async (pageNum = 1, silent = false, searchValue = '') => {
    // Incrementar ID de petición para detectar respuestas obsoletas
    const currentRequestId = ++requestIdRef.current;
    
    try {
      // Determinar qué tipo de loading mostrar
      if (!silent) {
        if (isInitialLoadRef.current) {
          // Carga inicial: spinner de página completa
          setLoading(true);
        } else {
          // Filtros/búsqueda/paginación: overlay sutil en la tabla
          setTableLoading(true);
        }
      }
      
      // Construir parámetros de la query
      const params = {
        page: pageNum,
        limit: PAGE_SIZE,
        sort_by: 'fecha_solicitud',
        sort_order: 'desc'
      };
      
      // Agregar filtros de URL
      if (urlEstado) params.estado = urlEstado;
      if (urlSupervisor) params.supervisor_id = urlSupervisor;
      if (urlProyecto) params.proyecto_id = urlProyecto;
      if (urlEtapa) params.etapa = urlEtapa;
      if (urlDias) params.dias = urlDias;
      if (urlOrigenes) params.origenes = urlOrigenes;
      
      // Agregar búsqueda si hay valor
      if (searchValue && searchValue.trim()) {
        params.search = searchValue.trim();
      }
      
      const response = await solicitudesAPI.getPaginated(params);
      
      // IMPORTANTE: Ignorar respuesta si hay una petición más reciente
      if (currentRequestId !== requestIdRef.current) {
        console.log(`Ignorando respuesta obsoleta (request ${currentRequestId}, actual ${requestIdRef.current})`);
        return;
      }
      
      const { data, pagination: pag } = response.data;
      
      setSolicitudes(data || []);
      setPagination(pag);
      
      // Marcar que la carga inicial ya pasó
      isInitialLoadRef.current = false;
    } catch (error) {
      // Ignorar errores de peticiones canceladas/obsoletas
      if (currentRequestId !== requestIdRef.current) return;
      if (!silent) toast.error('Error al cargar solicitudes');
      console.error('Error loading solicitudes:', error);
    } finally {
      // Solo quitar loading si es la petición actual
      if (currentRequestId === requestIdRef.current && !silent) {
        setLoading(false);
        setTableLoading(false);
      }
    }
  }, [urlEstado, urlSupervisor, urlProyecto, urlEtapa, urlOrigenes, urlDias]);

  // Cambio de página - usa el searchTerm actual
  const handlePageChange = useCallback((newPage) => {
    loadSolicitudes(newPage, false, searchTerm);
  }, [loadSolicitudes, searchTerm]);

  // Búsqueda con debounce - solución definitiva para race conditions
  const handleSearchChange = useCallback((value) => {
    // Actualizar estado para la UI y ref para callbacks
    setSearchTerm(value);
    searchTermRef.current = value;
    
    // Cancelar timeout anterior
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    
    // Debounce de 400ms - el requestIdRef en loadSolicitudes 
    // se encarga de ignorar respuestas obsoletas
    searchTimeoutRef.current = setTimeout(() => {
      loadSolicitudes(1, false, value);
    }, 400);
  }, [loadSolicitudes]);

  // Carga inicial - usa búsqueda de URL si existe
  useEffect(() => {
    loadSolicitudes(1, false, urlSearch || '');
  }, [urlEstado, urlSupervisor, urlProyecto, urlEtapa, urlOrigenes, urlDias]); // Recargar cuando cambian filtros URL

  // Polling inteligente + Refresh al volver a la pestaña
  useEffect(() => {
    // Función para iniciar polling
    const startPolling = () => {
      if (pollingIntervalRef.current) return; // Ya está activo
      
      pollingIntervalRef.current = setInterval(() => {
        if (isPollingActiveRef.current && document.visibilityState === 'visible') {
          // Usar searchTermRef para obtener el valor actual
          loadSolicitudes(pagination.page, true, searchTermRef.current);
        }
      }, POLLING_INTERVAL);
    };

    // Función para detener polling
    const stopPolling = () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
    };

    // Handler para cambio de visibilidad
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        // Usuario volvió a la pestaña: refresh inmediato + reanudar polling
        loadSolicitudes(pagination.page, true, searchTermRef.current);
        startPolling();
        isPollingActiveRef.current = true;
      } else {
        // Usuario salió de la pestaña: pausar polling
        isPollingActiveRef.current = false;
        stopPolling();
      }
    };

    // Iniciar polling al montar
    startPolling();
    
    // Escuchar cambios de visibilidad
    document.addEventListener('visibilitychange', handleVisibilityChange);

    // Cleanup al desmontar
    return () => {
      stopPolling();
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [loadSolicitudes, pagination.page]);

  // Open detail if urlId is set
  useEffect(() => {
    if (urlId && solicitudes.length > 0) {
      const found = solicitudes.find(s => s.id === urlId);
      if (found) {
        handleRowClick(found.id);
        // Clear the id param after opening
        const newParams = new URLSearchParams(searchParams);
        newParams.delete('id');
        setSearchParams(newParams, { replace: true });
      }
    }
  }, [urlId, solicitudes]);

  // Helper to get filter label
  const getFilterLabel = () => {
    const labels = [];
    if (urlEstado) {
      const estadoLabels = {
        'vigentes': 'Vigentes',
        'solicitado': 'Solicitadas',
        'inspeccion_pendiente': 'Insp. Pendiente',
        'inspeccion_programada': 'Insp. Programada',
        'atencion_pendiente': 'Atenc. Pendiente',
        'atencion_proceso': 'En Proceso',
        'stand_by_empresa': 'Stand By Empresa',
        'terminado': 'Terminados',
        'cerrado': 'Cerrados'
      };
      labels.push(estadoLabels[urlEstado] || urlEstado);
    }
    if (urlDias === 'lte30') labels.push('≤30 días');
    if (urlDias === 'gt30') labels.push('>30 días');
    return labels.join(' • ');
  };

  const clearUrlFilters = () => {
    setSearchParams({}, { replace: true });
  };

  const handleRowClick = async (solicitudId) => {
    if (!canView('/solicitudes') && !isAdmin()) {
      toast.error('No tiene permisos para ver detalles');
      return;
    }

    // Abrir diálogo INMEDIATAMENTE con spinner
    setShowDetail(true);
    setSelectedSolicitud({ loading: true }); // Indicador de carga

    try {
      // Usar endpoint optimizado que trae todo en una sola llamada
      const response = await solicitudesAPI.getDialogData(solicitudId);
      // El endpoint devuelve { requerimiento, supervisores, catalogos }
      setSelectedSolicitud({
        ...response.data.requerimiento,
        _dialogData: {
          supervisores: response.data.supervisores,
          catalogos: response.data.catalogos,
          recordatorio_autoagendamiento_activo: response.data.recordatorio_autoagendamiento_activo
        }
      });
    } catch (error) {
      toast.error('Error al cargar detalle');
      setShowDetail(false);
      setSelectedSolicitud(null);
    }
  };

  const [exportingExcel, setExportingExcel] = useState(false);

  const handleExportExcel = async () => {
    setExportingExcel(true);
    try {
      const response = await exportAPI.exportExcel();
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      // Extraer nombre del header Content-Disposition o generar localmente
      const disposition = response.headers?.['content-disposition'] || '';
      const filenameMatch = disposition.match(/filename=(.+?)(?:;|$)/);
      const now = new Date();
      const pad = (n) => String(n).padStart(2, '0');
      const fallbackName = `Requerimientos_Postventa_${pad(now.getDate())}.${pad(now.getMonth()+1)}.${now.getFullYear()}.${pad(now.getHours())}.${pad(now.getMinutes())}.xlsx`;
      link.setAttribute('download', filenameMatch ? filenameMatch[1] : fallbackName);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Excel descargado');
    } catch (error) {
      toast.error('Error al exportar Excel');
    } finally {
      setExportingExcel(false);
    }
  };

  const handleExportPDF = async () => {
    try {
      const response = await exportAPI.exportPDF();
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'requerimientos_postventa.pdf');
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('PDF descargado');
    } catch (error) {
      toast.error('Error al exportar PDF');
    }
  };

  const getEstadoBadge = (estado) => {
    const colores = {
      solicitado: 'bg-purple-100 text-purple-700',
      inspeccion_pendiente: 'bg-gray-100 text-gray-700',
      inspeccion_programada: 'bg-gray-100 text-gray-700',
      atencion_pendiente: 'bg-yellow-100 text-yellow-700',
      atencion_proceso: 'bg-yellow-100 text-yellow-700',
      stand_by_empresa: 'bg-slate-200 text-slate-600',
      terminado: 'bg-blue-100 text-blue-700',
      cerrado: 'bg-green-100 text-green-700',
    };
    return colores[estado] || 'bg-gray-100 text-gray-700';
  };

  const getPasoSiguiente = (estado, solicitud) => {
    if (estado === 'inspeccion_programada') {
      // Si todas las solicitudes son "no procede" y ya tiene acta/correo → mostrar "Terminar inspección"
      if (solicitud.todas_no_procede && (
        solicitud.inspeccion_acta_url || 
        ['enviado_correo', 'aprobado_correo'].includes(solicitud.inspeccion_acta_estado)
      )) {
        return { texto: 'Terminar inspección', color: 'bg-blue-500 hover:bg-blue-600' };
      }
      return { texto: 'Realizar inspección', color: 'bg-gray-500 hover:bg-gray-600' };
    }
    
    const pasos = {
      solicitado: { texto: 'Agendar inspección', color: 'bg-purple-500 hover:bg-purple-600' },
      inspeccion_pendiente: { texto: 'Agendar inspección', color: 'bg-gray-500 hover:bg-gray-600' },
      atencion_pendiente: { texto: 'Realizar atención', color: 'bg-yellow-500 hover:bg-yellow-600' },
      atencion_proceso: { texto: 'Terminar', color: 'bg-blue-500 hover:bg-blue-600' },
      stand_by_empresa: { texto: 'Pausado', color: '', disabled: true },
      terminado: null,
      cerrado: null,
    };
    return pasos[estado] || null;
  };

  const handlePasoSiguiente = async (solicitudId, estado, e) => {
    e.stopPropagation();
    
    // Activar spinner para este botón específico
    setLoadingPasoSiguiente(solicitudId);
    
    // Abrir diálogo INMEDIATAMENTE con spinner
    setSelectedSolicitud({ loading: true });
    
    try {
      if (estado === 'solicitado' || estado === 'inspeccion_pendiente') {
        // Usar getDialogData para traer la flag recordatorio_autoagendamiento_activo
        const response = await solicitudesAPI.getDialogData(solicitudId);
        setSelectedSolicitud({
          ...response.data.requerimiento,
          _dialogData: {
            supervisores: response.data.supervisores,
            catalogos: response.data.catalogos,
            recordatorio_autoagendamiento_activo: response.data.recordatorio_autoagendamiento_activo
          }
        });
        setShowDetail(true);
      } else {
        const response = await solicitudesAPI.getOne(solicitudId);
        setSelectedSolicitud(response.data);
        
        if (estado === 'inspeccion_programada') {
          setShowInspeccion(true);
        } else if (estado === 'atencion_pendiente' || estado === 'atencion_proceso') {
          setShowAtencion(true);
        } else {
          setShowDetail(true);
        }
      }
    } catch (error) {
      toast.error('Error al cargar el requerimiento');
      setSelectedSolicitud(null);
    } finally {
      // Desactivar spinner del botón
      setLoadingPasoSiguiente(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-10 w-10 lg:h-12 lg:w-12 border-b-2 border-[#9d8272] mx-auto"></div>
          <p className="mt-4 text-sm lg:text-base text-[#5A5A5A]">Cargando...</p>
        </div>
      </div>
    );
  }

  const canCreateSolicitud = canCreate('/solicitudes') || isAdmin();
  const canViewSolicitud = canView('/solicitudes') || isAdmin();

  if (!canViewSolicitud) {
    return (
      <div className="flex items-center justify-center h-64 px-4">
        <Card className="bg-white border border-[#E8E8E7] shadow-sm p-4 lg:p-6">
          <p className="text-sm lg:text-base text-[#888888]">No tiene permisos para ver esta página</p>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-4 lg:space-y-6" data-testid="solicitudes-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-[#2D2D2D]" style={{ fontFamily: 'Playfair Display, serif' }}>
            Requerimientos
          </h1>
          <p className="text-sm lg:text-lg text-[#5A5A5A] mt-1 hidden sm:block">Gestiona los requerimientos de postventa</p>
        </div>
        <div className="flex flex-wrap gap-2">
          {canViewSolicitud && (
            <>
              <Button
                onClick={handleExportExcel}
                variant="outline"
                size="sm"
                className="border-[#9d8272] text-[#9d8272] hover:bg-[#9d8272] hover:text-white text-xs lg:text-sm"
                data-testid="export-excel-button"
                disabled={exportingExcel}
              >
                {exportingExcel ? <Loader2 size={14} className="mr-1 lg:mr-2 animate-spin" /> : <Download size={14} className="mr-1 lg:mr-2" />}
                <span className="hidden sm:inline">{exportingExcel ? 'Descargando...' : 'Excel'}</span>
              </Button>
              <Button
                onClick={handleExportPDF}
                variant="outline"
                size="sm"
                className="border-[#8c926f] text-[#8c926f] hover:bg-[#8c926f] hover:text-white text-xs lg:text-sm"
                data-testid="export-pdf-button"
              >
                <FileText size={14} className="mr-1 lg:mr-2" />
                <span className="hidden sm:inline">PDF</span>
              </Button>
            </>
          )}
          {canCreateSolicitud && (
            <Button
              onClick={() => setShowCreate(true)}
              size="sm"
              className="bg-[#9d8272] hover:bg-[#8a7162] text-white text-xs lg:text-sm"
              data-testid="create-solicitud-button"
            >
              <Plus size={14} className="mr-1 lg:mr-2" />
              Nuevo
            </Button>
          )}
        </div>
      </div>

      {/* URL Filter Indicator */}
      {hasUrlFilters && (
        <div className="flex items-center gap-2 p-3 bg-[#f0f9ff] border border-[#bae6fd] rounded-lg">
          <Filter size={16} className="text-[#0284c7]" />
          <span className="text-sm text-[#0369a1] flex-1">
            Filtrado por: <span className="font-medium">{getFilterLabel()}</span>
            {pagination.total > 0 && (
              <span className="ml-2 text-[#0284c7]">({pagination.total} resultados)</span>
            )}
          </span>
          <button
            onClick={clearUrlFilters}
            className="flex items-center gap-1 px-2 py-1 text-xs text-[#0369a1] hover:bg-[#e0f2fe] rounded transition-colors"
          >
            <X size={14} />
            Limpiar
          </button>
        </div>
      )}

      {/* Search Bar */}
      <div className="sticky top-0 z-40 -mx-4 px-4 py-3 bg-gradient-to-b from-[#FAFAFA] via-[#FAFAFA] to-transparent sm:static sm:mx-0 sm:px-0 sm:py-0 sm:bg-none">
        <div className="max-w-2xl mx-auto sm:mx-0">
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => handleSearchChange(e.target.value)}
            placeholder="Buscar por código, proyecto, cliente, supervisor, estado, manual/auto..."
            className="w-full px-4 py-2 border border-[#E8E8E7] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#9d8272] focus:border-transparent text-sm"
            data-testid="search-input"
          />
        </div>
      </div>

      {/* Desktop Table - Hidden on mobile */}
      <Card className="bg-white border border-[#E8E8E7] shadow-sm hidden lg:block">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-lg lg:text-xl font-semibold text-[#2D2D2D]">
              Listado de Requerimientos
            </CardTitle>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => loadSolicitudes(pagination.page, false, searchTermRef.current)}
              disabled={loading || tableLoading}
              className="text-[#9d8272] hover:text-[#7a6459] hover:bg-[#f5f0eb]"
              data-testid="refresh-requerimientos-btn"
            >
              <RefreshCw size={18} className={(loading || tableLoading) ? 'animate-spin' : ''} />
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto relative">
            {/* Overlay sutil durante búsqueda/filtros */}
            {tableLoading && (
              <div className="absolute inset-0 bg-white/60 flex items-center justify-center z-10 rounded-lg">
                <div className="flex items-center gap-2 text-[#9d8272]">
                  <Loader2 size={20} className="animate-spin" />
                  <span className="text-sm">Buscando...</span>
                </div>
              </div>
            )}
            <table className={`w-full transition-opacity duration-200 ${tableLoading ? 'opacity-50' : 'opacity-100'}`} data-testid="solicitudes-table">
              <thead>
                <tr className="border-b border-[#E8E8E7] text-left text-sm">
                  <th className="pb-2 font-semibold text-[#5A5A5A]">Código</th>
                  <th className="pb-2 font-semibold text-[#5A5A5A]">Proyecto</th>
                  <th className="pb-2 font-semibold text-[#5A5A5A]">Depto.</th>
                  <th className="pb-2 font-semibold text-[#5A5A5A] text-center">Sol.</th>
                  <th className="pb-2 font-semibold text-[#5A5A5A]">Estado</th>
                  <th className="pb-2 font-semibold text-[#5A5A5A]">Paso Siguiente</th>
                  <th className="pb-2 font-semibold text-[#5A5A5A]">Supervisor</th>
                  <th className="pb-2 font-semibold text-[#5A5A5A] text-center">Prog</th>
                  <th className="pb-2 font-semibold text-[#5A5A5A]">Titular</th>
                  <th className="pb-2 font-semibold text-[#5A5A5A]">Fecha</th>
                  <th className="pb-2 font-semibold text-[#5A5A5A]">Fecha Cierre</th>
                </tr>
              </thead>
              <tbody>
                {solicitudes.map((solicitud) => {
                  const pasoSiguiente = getPasoSiguiente(solicitud.estado, solicitud);
                  return (
                    <tr
                      key={solicitud.id}
                      className="border-b border-[#E8E8E7] hover:bg-[#F5F5F5] transition-colors text-sm h-12"
                      data-testid="solicitud-row"
                    >
                      <td className="py-2 text-[#9d8272] font-bold cursor-pointer align-middle" onClick={() => handleRowClick(solicitud.id)}>
                        <div className="truncate max-w-[100px]">#{solicitud.codigo}</div>
                      </td>
                      <td className="py-2 text-[#2D2D2D] font-medium cursor-pointer align-middle" onClick={() => handleRowClick(solicitud.id)}>
                        <div className="truncate max-w-[150px]" title={solicitud.proyecto_nombre}>
                          {solicitud.proyecto_nombre}
                        </div>
                      </td>
                      <td className="py-2 text-[#5A5A5A] cursor-pointer align-middle" onClick={() => handleRowClick(solicitud.id)}>
                        <div className="truncate max-w-[120px]" title={solicitud.departamento}>
                          {solicitud.departamento}
                        </div>
                      </td>
                      <td className="py-2 text-center cursor-pointer align-middle" onClick={() => handleRowClick(solicitud.id)}>
                        <span className="inline-block bg-[#9d8272] text-white px-2 py-0.5 rounded-full text-xs font-medium leading-none">
                          {solicitud.num_solicitudes || 0}
                        </span>
                      </td>
                      <td className="py-2 cursor-pointer align-middle" onClick={() => handleRowClick(solicitud.id)}>
                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium whitespace-nowrap ${getEstadoBadge(solicitud.estado)}`}>
                          {solicitud.estado.replace('_', ' ')}
                          {solicitud.estado === 'terminado' && solicitud.todas_no_procede && (
                            <Zap size={10} className="text-blue-500" title="Terminado desde inspección" />
                          )}
                        </span>
                      </td>
                      <td className="py-2 align-middle">
                        {pasoSiguiente ? (
                          pasoSiguiente.disabled ? (
                            <span className="px-2.5 py-1 rounded text-xs font-medium text-gray-400 bg-gray-100 whitespace-nowrap">
                              {pasoSiguiente.texto}
                            </span>
                          ) : (
                          <button
                            onClick={(e) => handlePasoSiguiente(solicitud.id, solicitud.estado, e)}
                            disabled={loadingPasoSiguiente === solicitud.id}
                            className={`px-2.5 py-1 rounded text-xs font-medium text-white whitespace-nowrap ${pasoSiguiente.color} transition-colors disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center gap-1.5`}
                          >
                            {loadingPasoSiguiente === solicitud.id ? (
                              <>
                                <Loader2 size={12} className="animate-spin" />
                                <span>Cargando...</span>
                              </>
                            ) : (
                              pasoSiguiente.texto
                            )}
                          </button>
                          )
                        ) : (
                          <span className="text-[#888888]">—</span>
                        )}
                      </td>
                      <td className="py-2 text-[#5A5A5A] cursor-pointer align-middle" onClick={() => handleRowClick(solicitud.id)}>
                        <div className="truncate max-w-[140px]" title={solicitud.supervisor_nombre || 'Sin asignar'}>
                          {solicitud.supervisor_nombre || <span className="text-[#888888] italic text-xs">Sin asignar</span>}
                        </div>
                      </td>
                      <td className="py-2 text-center align-middle">
                        {solicitud.programacion_tipo ? (
                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-xs font-medium cursor-default ${
                                  solicitud.programacion_tipo === 'manual' 
                                    ? 'bg-indigo-50 text-indigo-700' 
                                    : 'bg-teal-50 text-teal-700'
                                }`}>
                                  <CalendarClock size={10} />
                                  {solicitud.programacion_tipo === 'manual' ? 'Manual' : 'Auto'}
                                </span>
                              </TooltipTrigger>
                              <TooltipContent side="top" className="text-xs max-w-[220px]">
                                <p className="font-semibold">{solicitud.programado_por_nombre || '—'}</p>
                                {solicitud.programado_en && (
                                  <p className="opacity-80">
                                    {new Date(solicitud.programado_en).toLocaleString('es-PE', { dateStyle: 'short', timeStyle: 'short' })}
                                  </p>
                                )}
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        ) : (
                          <span className="text-[#888888]">—</span>
                        )}
                      </td>
                      <td className="py-2 text-[#5A5A5A] cursor-pointer align-middle" onClick={() => handleRowClick(solicitud.id)}>
                        <div className="truncate max-w-[140px]" title={solicitud.cliente_nombre}>
                          {solicitud.cliente_nombre}
                        </div>
                      </td>
                      <td className="py-2 text-[#888888] text-xs cursor-pointer align-middle whitespace-nowrap" onClick={() => handleRowClick(solicitud.id)}>
                        {new Date(solicitud.fecha_solicitud).toLocaleDateString('es-PE')}
                      </td>
                      <td className="py-2 text-[#888888] text-xs cursor-pointer align-middle whitespace-nowrap" onClick={() => handleRowClick(solicitud.id)}>
                        {solicitud.fecha_cierre ? new Date(solicitud.fecha_cierre).toLocaleDateString('es-PE') : <span className="text-[#888888]">—</span>}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            {solicitudes.length === 0 && (
              <div className="text-center py-12 text-[#888888]">
                <ClipboardList size={48} className="mx-auto mb-4 opacity-50" />
                <p>{pagination.total === 0 ? 'No hay requerimientos registrados' : 'No se encontraron resultados'}</p>
              </div>
            )}
            
            {/* Paginación Desktop */}
            {solicitudes.length > 0 && (
              <Pagination
                page={pagination.page}
                totalPages={pagination.total_pages}
                total={pagination.total}
                limit={pagination.limit}
                hasNext={pagination.has_next}
                hasPrev={pagination.has_prev}
                onPageChange={handlePageChange}
                loading={loading || tableLoading}
              />
            )}
          </div>
        </CardContent>
      </Card>

      {/* Mobile Cards - Hidden on desktop */}
      <div className="lg:hidden space-y-3 relative">
        {/* Overlay sutil para móvil durante búsqueda/filtros */}
        {tableLoading && (
          <div className="absolute inset-0 bg-white/60 flex items-center justify-center z-10 rounded-lg min-h-[200px]">
            <div className="flex items-center gap-2 text-[#9d8272]">
              <Loader2 size={20} className="animate-spin" />
              <span className="text-sm">Buscando...</span>
            </div>
          </div>
        )}
        {/* Mobile Header with Refresh */}
        <div className="flex items-center justify-between px-1">
          <h2 className="text-base font-semibold text-[#2D2D2D]">
            Requerimientos
          </h2>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => loadSolicitudes(pagination.page, false, searchTermRef.current)}
            disabled={loading || tableLoading}
            className="text-[#9d8272] hover:text-[#7a6459] hover:bg-[#f5f0eb]"
            data-testid="refresh-requerimientos-mobile-btn"
          >
            <RefreshCw size={18} className={(loading || tableLoading) ? 'animate-spin' : ''} />
          </Button>
        </div>
        <div className={`transition-opacity duration-200 ${tableLoading ? 'opacity-50' : 'opacity-100'}`}>
        {solicitudes.length === 0 ? (
          <Card className="bg-white border border-[#E8E8E7] shadow-sm">
            <CardContent className="py-8">
              <div className="text-center text-[#888888]">
                <ClipboardList size={40} className="mx-auto mb-3 opacity-50" />
                <p className="text-sm">{pagination.total === 0 ? 'No hay requerimientos registrados' : 'No se encontraron resultados'}</p>
              </div>
            </CardContent>
          </Card>
        ) : (
          <>
          {solicitudes.map((solicitud) => {
            const pasoSiguiente = getPasoSiguiente(solicitud.estado, solicitud);
            return (
              <Card 
                key={solicitud.id}
                className="bg-white border border-[#E8E8E7] shadow-sm active:bg-gray-50"
                onClick={() => handleRowClick(solicitud.id)}
                data-testid={`solicitud-card-${solicitud.id}`}
              >
                <CardContent className="p-3">
                  {/* Top row: Code and Status */}
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-[#9d8272] font-bold text-sm">#{solicitud.codigo}</span>
                    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${getEstadoBadge(solicitud.estado)}`}>
                      {solicitud.estado.replace('_', ' ')}
                      {solicitud.estado === 'terminado' && solicitud.todas_no_procede && (
                        <Zap size={10} className="text-blue-500" title="Terminado desde inspección" />
                      )}
                    </span>
                  </div>
                  
                  {/* Project and Department */}
                  <div className="mb-2">
                    <p className="text-[#2D2D2D] font-medium text-sm truncate">{solicitud.proyecto_nombre}</p>
                    <p className="text-[#5A5A5A] text-xs">Depto. {solicitud.departamento} • {solicitud.num_solicitudes || 0} solicitud(es)</p>
                  </div>
                  
                  {/* Titular and Date */}
                  <div className="flex items-center justify-between text-xs text-[#888888] mb-2">
                    <span className="truncate mr-2">{solicitud.cliente_nombre}</span>
                    <span>{new Date(solicitud.fecha_solicitud).toLocaleDateString('es-PE')}</span>
                  </div>
                  {solicitud.fecha_cierre && (
                    <div className="flex items-center justify-between text-xs text-[#888888] mb-2">
                      <span>Fecha Cierre:</span>
                      <span>{new Date(solicitud.fecha_cierre).toLocaleDateString('es-PE')}</span>
                    </div>
                  )}
                  
                  {/* Supervisor */}
                  {solicitud.supervisor_nombre && (
                    <p className="text-xs text-[#5A5A5A] mb-2">
                      Supervisor: <span className="font-medium">{solicitud.supervisor_nombre}</span>
                    </p>
                  )}
                  
                  {/* Programación */}
                  {solicitud.programacion_tipo && (
                    <div className="flex items-center gap-1 text-xs text-[#5A5A5A] mb-2">
                      <CalendarClock size={10} />
                      <span>Prog: <span className="font-medium">{solicitud.programacion_tipo === 'manual' ? 'Manual' : 'Auto'}</span></span>
                      {solicitud.programado_por_nombre && (
                        <span className="text-[#888888]">por {solicitud.programado_por_nombre}</span>
                      )}
                    </div>
                  )}
                  
                  {/* Action Button */}
                  <div className="flex items-center justify-between pt-2 border-t border-[#E8E8E7]">
                    {pasoSiguiente ? (
                      pasoSiguiente.disabled ? (
                        <span className="px-3 py-1.5 rounded text-xs font-medium text-gray-400 bg-gray-100">
                          {pasoSiguiente.texto}
                        </span>
                      ) : (
                      <button
                        onClick={(e) => handlePasoSiguiente(solicitud.id, solicitud.estado, e)}
                        disabled={loadingPasoSiguiente === solicitud.id}
                        className={`px-3 py-1.5 rounded text-xs font-medium text-white ${pasoSiguiente.color} transition-colors disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center gap-1.5`}
                      >
                        {loadingPasoSiguiente === solicitud.id ? (
                          <>
                            <Loader2 size={12} className="animate-spin" />
                            <span>Cargando...</span>
                          </>
                        ) : (
                          pasoSiguiente.texto
                        )}
                      </button>
                      )
                    ) : (
                      <span className="text-[#888888] text-xs">Completado</span>
                    )}
                    <ChevronRight size={18} className="text-[#888888]" />
                  </div>
                </CardContent>
              </Card>
            );
          })}
          
          {/* Paginación Mobile */}
          <Pagination
            page={pagination.page}
            totalPages={pagination.total_pages}
            total={pagination.total}
            limit={pagination.limit}
            hasNext={pagination.has_next}
            hasPrev={pagination.has_prev}
            onPageChange={handlePageChange}
            loading={loading || tableLoading}
          />
          </>
        )}
        </div>
      </div>

      {/* Dialogs */}
      {showDetail && selectedSolicitud && (
        <SolicitudDetailDialog
          solicitud={selectedSolicitud}
          open={showDetail}
          onClose={() => {
            setShowDetail(false);
            loadSolicitudes(pagination.page, true, searchTermRef.current);
          }}
          onUpdate={() => {
            loadSolicitudes(pagination.page, true, searchTermRef.current);
          }}
        />
      )}

      {showCreate && (
        <CreateSolicitudDialog
          open={showCreate}
          onClose={() => {
            setShowCreate(false);
            loadSolicitudes(1, false, searchTermRef.current);
          }}
        />
      )}

      {showInspeccion && selectedSolicitud && (
        <InspeccionDialog
          requerimiento={selectedSolicitud}
          open={showInspeccion}
          onClose={() => {
            setShowInspeccion(false);
            loadSolicitudes(pagination.page, true, searchTermRef.current);
          }}
          onUpdate={() => loadSolicitudes(pagination.page, true, searchTermRef.current)}
        />
      )}

      {showAtencion && selectedSolicitud && (
        <AtencionDialog
          requerimiento={selectedSolicitud}
          open={showAtencion}
          onClose={() => {
            setShowAtencion(false);
            loadSolicitudes(pagination.page, true, searchTermRef.current);
          }}
          onUpdate={() => loadSolicitudes(pagination.page, true, searchTermRef.current)}
        />
      )}
    </div>
  );
};

export default Solicitudes;
